/**
 * Centralized storage key management
 * Prevents key collisions and provides type safety
 * 
 * @author Varun Pandey
 */

export const STORAGE_KEYS = {
  // User & Auth
  USER_DATA: 'userData',
  /** Session storage key: message to show on login page after redirect (e.g. session expired) */
  LOGIN_REDIRECT_MESSAGE: 'loginRedirectMessage',
  /** Session storage key: success toast message shown on login page after explicit logout. */
  LOGOUT_SUCCESS_MESSAGE: 'logoutSuccessMessage',
  /** Session storage key: suppresses session-expired toast for intentional auth redirects. */
  SUPPRESS_LOGIN_REDIRECT_TOAST: 'suppressLoginRedirectToast',
  /** Session storage: set on successful login before navigating to user-home. */
  LOGIN_TO_USER_HOME: 'loginToUserHome',
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  BIOMETRIC_ENABLED: 'biometricEnabled',
  /** Set after first post-onboarding land on user-home; used to show confetti once */
  FIRST_TIME_USER_HOME: 'firstTimeUserHome',
  /** Session storage: last selected tab on Event Essentials (pricing|billing|secondary|tech). Cleared on logout. */
  EVENT_ESSENTIALS_ACTIVE_TAB: 'eventEssentialsActiveTab',

  // Instagram (user-specific to prevent cross-account contamination)
  INSTAGRAM_STATE: (userId: string) => `instagram-storage-${userId}`,
  INSTAGRAM_TOKEN: (userId: string) => `instagram-token-${userId}`,
  INSTAGRAM_MEDIA: (userId: string) => `instagram-media-${userId}`,
  INSTAGRAM_STATS: (userId: string) => `instagram-stats-${userId}`,
  
  // OAuth State
  OAUTH_STATE: 'oauthState',
  OAUTH_CONTEXT: 'oauthContext',
  
  // Profile
  PROFILE_DATA: (userId: string) => `profile-${userId}`,
  PROFILE_COMPLETION: (userId: string) => `profile-completion-${userId}`,
  
  // Wallet & Payments
  WALLET_BALANCE: (userId: string) => `wallet-balance-${userId}`,
  TRANSACTION_HISTORY: (userId: string) => `transactions-${userId}`,
  
  // Subscription
  SUBSCRIPTION_DATA: (userId: string) => `subscription-${userId}`,
  
  // Bookings
  BOOKINGS_CACHE: (userId: string) => `bookings-${userId}`,
  /** Session storage: in-progress Booking Fill form draft, scoped by booking form id. */
  BOOKING_FILL_DRAFT: (formId: string | number) => `booking-fill-draft-${formId}`,
  /** Per-artist map of event edge notifications already played (start/end). */
  EXPRESS_EVENT_SOUND_MARKERS: (artistKey: string) => `express-event-sound-markers-${artistKey}`,
  /** Per-artist flag to avoid repeatedly requesting local notification permissions. */
  EXPRESS_EVENT_NOTIFICATIONS_PERMISSION_ASKED: (artistKey: string) =>
    `express-event-notifications-permission-asked-${artistKey}`,
  
  // Notifications
  /** Preferences flag: OneSignal push permission prompt already shown once. */
  ONESIGNAL_PERMISSION_PROMPTED: 'onesignalPermissionPrompted',
  NOTIFICATIONS: (userId: string) => `notifications-${userId}`,
  
  // App Settings
  APP_SETTINGS: 'appSettings',
  THEME: 'theme',
  LANGUAGE: 'language',
  
  // Form Drafts
  FORM_DRAFT: (formId: string) => `form-draft-${formId}`,
  
  // Storage Version (for migrations)
  STORAGE_VERSION: 'storageVersion',
  
  // Signup / Onboarding
  /** Local-only flag to resume multi-step signup flow (steps 4–6). Cleared after onboarding completes. */
  SIGNUP_PROGRESS: 'signupProgress',
  /** Name/email captured from native Sign in with Apple (first authorization). */
  APPLE_SIGNIN_PROFILE: 'appleSignInProfile',

  // User Home nudges (artist-specific)
  /** Last time (ms epoch) the membership upsell sheet was auto-shown on user-home for this artist. */
  MEMBERSHIP_NUDGE_LAST_SHOWN: (artistKey: string) => `membership-nudge-last-shown-${artistKey}`,
} as const;

export type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS] | string;
